import {
  trackComponentView,
  triggerPageExit,
} from "@tm/insurance/analytics/xdm";
import { navigationActions } from "@tm/insurance/navigation";
import { getCookieByName } from "@tm/insurance/utils";
import { MoreBenefits } from "@tm/static";
import { usePlanType } from "@tm/ui";
import { storePageTransitionStart } from "@tm/utility/analytics";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getMyCashState, setMyCashSessionState } from "../../sessionStore";
import { trackListingClick, trackListingView } from "../../trackingUtils";
import { buildTravellerDetailsParams, toMoreBenefitsData } from "../../utils";

import CoachMark from "../AmtAttach/CoachMark";
import TripOptions from "../AmtAttach/TripOptions";
import { PlanCardsShimmer } from "../ListingShimmer/ListingShimmer";
import Card from "./Card";
import Header from "./Header";
import MyCashCallout from "./MyCashCallout";
import {
  CardsGrid,
  CardsScrollContainer,
  HeaderAndCardsBlock,
  PlanCardsInner,
  PlanCardsOuter,
  ScrollArrow,
  TopRow,
} from "./PlanCards.styles";
import { IBenefitsBottomsheet, IPlanCardsProps, MyCashState } from "./types";

const CHEVRON_LEFT = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M10 12L6 8l4-4"
      stroke="#333"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CHEVRON_RIGHT = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M6 4l4 4-4 4"
      stroke="#333"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const COACH_MARK_DISMISSED_KEY = "listing_coachmark_dismissed";

const PlanCards = ({
  myCashData,
  currentState,
  plansHeaderData,
  compareCta,
  cards,
  onLoginRequest,
  onCompareClick,
  pageTracked,
  tripOptions,
  selectedTripOption,
  onTripOptionChange,
  onInfoIconClick,
  isLoading,
}: IPlanCardsProps) => {
  const [myCashState, setMyCashState] = useState<MyCashState>(() =>
    getMyCashState(currentState || "loggedOut")
  );
  const { type } = usePlanType();

  useEffect(() => {
    const hasAuth =
      getCookieByName("mmt-auth") || getCookieByName("OAUTH-GOIBIBO");
    if (!hasAuth) {
      setMyCashState("loggedOut");
    } else {
      setMyCashState((prev) => (prev === "loggedOut" ? "unApplied" : prev));
    }
  }, []);

  useEffect(() => {
    setMyCashSessionState(myCashState);
  }, [myCashState]);

  const hasInitializedAuth = useRef(false);
  useEffect(() => {
    if (!hasInitializedAuth.current) {
      hasInitializedAuth.current = true;
      return;
    }
    if (myCashState !== "loggedOut" && myCashData) {
      trackListingClick("logged_out_logged_in", type);
    }
  }, [myCashState]);

  const wasDismissed = useMemo(() => {
    try {
      return sessionStorage.getItem(COACH_MARK_DISMISSED_KEY) === "true";
    } catch {
      return false;
    }
  }, []);

  const [showCoachMark, setShowCoachMark] = useState(false);

  const popOverData = useMemo(() => {
    if (wasDismissed || !tripOptions?.length) return null;
    for (const opt of tripOptions) {
      if (opt.popOverData?.title || opt.popOverData?.subTitle) {
        return opt.popOverData;
      }
    }
    return null;
  }, [tripOptions, wasDismissed]);

  useEffect(() => {
    if (!popOverData || wasDismissed) return;
    const timer = setTimeout(() => setShowCoachMark(true), 5000);
    return () => clearTimeout(timer);
  }, [popOverData, wasDismissed]);

  const handleDismissCoachMark = useCallback(() => {
    setShowCoachMark(false);
    try {
      sessionStorage.setItem(COACH_MARK_DISMISSED_KEY, "true");
    } catch {
      // sessionStorage unavailable
    }
  }, []);

  const [activeBenefits, setActiveBenefits] =
    useState<IBenefitsBottomsheet | null>(null);

  const validCards = useMemo(() => cards?.filter(Boolean) ?? [], [cards]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const tolerance = 2;
    setCanScrollLeft(el.scrollLeft > tolerance);
    setCanScrollRight(
      el.scrollLeft + el.clientWidth < el.scrollWidth - tolerance
    );
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState, validCards.length]);

  const handleScrollBy = useCallback((direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const firstCard = el.firstElementChild as HTMLElement | null;
    const cardWidth = firstCard
      ? firstCard.offsetWidth + 24
      : el.clientWidth / 4;
    el.scrollBy({ left: direction === "right" ? cardWidth : -cardWidth });
  }, []);

  const handleMyCashCtaClick = () => {
    const actionMap: Record<MyCashState, string> = {
      loggedOut: "login_clicked",
      unApplied: "mycash_applied",
      applied: "mycash_removed",
    };
    trackListingClick(actionMap[myCashState], type);

    if (myCashState === "loggedOut") {
      trackListingClick("login_modal_shown", type);
      onLoginRequest?.();
    } else if (myCashState === "unApplied") {
      setMyCashState("applied");
    } else {
      setMyCashState("unApplied");
    }
  };

  const handleBuyNow = useCallback(
    (quoteId: string, productId?: number, isRecommended?: boolean) => {
      if (!quoteId) return;
      const params = buildTravellerDetailsParams(
        productId ?? "",
        quoteId,
        myCashState === "applied"
      );
      const productComponents = [{ product_list: [{ id: String(productId) }] }];
      trackListingClick(`buy_now_${productId}_clicked`, type, {
        components: productComponents,
      });
      if (isRecommended) {
        trackListingClick(`recommended_product_${productId}_clicked`, type, {
          components: productComponents,
        });
      }
      triggerPageExit();
      storePageTransitionStart("listing");
      navigationActions.navigateToTravellerDetails(params);
    },
    [type, myCashState]
  );

  const handleViewBenefits = useCallback(
    (planId: string) => {
      const card = validCards.find((c) => c.planId === planId);
      if (card?.benefitsBottomsheet) {
        setActiveBenefits(card.benefitsBottomsheet);
        trackListingClick("view_all_benefits_clicked", type);
      }
    },
    [validCards, type]
  );

  const moreBenefitsData = useMemo(
    () => (activeBenefits ? toMoreBenefitsData(activeBenefits) : null),
    [activeBenefits]
  );

  // ──── Component View: MyCash section ────
  useEffect(() => {
    if (!pageTracked || !myCashData) return;
    trackListingView("my_cash_section_shown", type);
  }, [pageTracked, myCashData, type]);

  // ──── Component View: MyCash apply CTA shown ────
  useEffect(() => {
    if (!pageTracked || !myCashData || myCashState !== "unApplied") return;
    trackListingView("mycash_apply_shown", type);
  }, [pageTracked, myCashData, myCashState, type]);

  // ──── Component View: MyCash remove CTA shown ────
  useEffect(() => {
    if (!pageTracked || !myCashData || myCashState !== "applied") return;
    trackListingView("mycash_remove_shown", type);
  }, [pageTracked, myCashData, myCashState, type]);

  // ──── Component View: Login CTA shown ────
  useEffect(() => {
    if (!pageTracked || !myCashData || myCashState !== "loggedOut") return;
    trackListingView("login_shown", type);
  }, [pageTracked, myCashData, myCashState, type]);

  // ──── Component View: Plan cards seen ────
  useEffect(() => {
    if (!pageTracked || !validCards.length) return;
    try {
      validCards.forEach((card) => {
        if (!card?.planId) return;
        trackListingView(`${card.planId}_plan_card_shown`, type);
        if (card.badge?.text) {
          trackListingView(`recommended_product_${card.planId}`, type);
        }
        card.previewBenefits?.forEach((benefit) => {
          if (!benefit?.title) return;
          trackComponentView({
            name: `${benefit.title}_seen`,
            type: "component",
          });
        });
      });
    } catch (err) {
      console.error("Plan card view tracking failed", err);
    }
  }, [pageTracked, validCards, type]);

  if (!plansHeaderData && validCards.length === 0 && !myCashData) return null;

  return (
    <PlanCardsOuter>
      <PlanCardsInner>
        <TopRow>
          <MyCashCallout
            myCashData={myCashData}
            myCashState={myCashState}
            onCtaClick={handleMyCashCtaClick}
          />
          {tripOptions?.length ? (
            <TripOptions
              options={tripOptions}
              selectedOptionId={selectedTripOption}
              onOptionChange={onTripOptionChange}
              onInfoIconClick={onInfoIconClick}
            />
          ) : null}
          {showCoachMark && popOverData && (
            <CoachMark {...popOverData} onDismiss={handleDismissCoachMark} />
          )}
        </TopRow>
        <HeaderAndCardsBlock>
          {isLoading ? (
            <PlanCardsShimmer />
          ) : (
            <>
              <Header
                data={plansHeaderData}
                compareCta={compareCta}
                onCompareClick={onCompareClick}
              />
              {validCards.length > 0 && (
                <CardsScrollContainer>
                  {canScrollLeft && (
                    <ScrollArrow
                      $direction="left"
                      onClick={() => handleScrollBy("left")}
                      aria-label="Scroll plans left"
                    >
                      {CHEVRON_LEFT}
                    </ScrollArrow>
                  )}
                  <CardsGrid ref={scrollRef}>
                    {validCards.map((card, index) => (
                      <Card
                        key={card.planId || index}
                        {...card}
                        myCashState={myCashState}
                        onBuyNow={handleBuyNow}
                        onViewBenefits={handleViewBenefits}
                      />
                    ))}
                  </CardsGrid>
                  {canScrollRight && (
                    <ScrollArrow
                      $direction="right"
                      onClick={() => handleScrollBy("right")}
                      aria-label="Scroll plans right"
                    >
                      {CHEVRON_RIGHT}
                    </ScrollArrow>
                  )}
                </CardsScrollContainer>
              )}
            </>
          )}
        </HeaderAndCardsBlock>
      </PlanCardsInner>

      {activeBenefits && moreBenefitsData && (
        <MoreBenefits
          moreBenfitsTitle={activeBenefits.title}
          data={moreBenefitsData as Parameters<typeof MoreBenefits>[0]["data"]}
          handleClose={() => setActiveBenefits(null)}
          handleBuyButton={() => {
            const card = validCards.find(
              (c) => c.benefitsBottomsheet === activeBenefits
            );
            if (card?.planId) {
              handleBuyNow(card.planId, card.productId);
            }
          }}
          v2
        />
      )}
    </PlanCardsOuter>
  );
};

export default PlanCards;
